import type {
  ConsentAction,
  ConsentCategory,
  ConsentRecord,
  ConsentState,
} from './types';

// CONSTANTS
const COOKIE_NAME = '__rm_consent';
const COOKIE_VERSION = 1;
const COOKIE_EXPIRY_DAYS = 365;
// ----------------- MODULE-SCOPED VARIABLES -----------------
// These variables are the single source of truth for consent state.
// Nothing outside this module touches them directly.

let _state: ConsentState = { status: 'undecided' };
let _listeners: Array<(state: ConsentState) => void> = [];
let _sessionId: string | null = null;

// ----------------- HELPER FUNCTIONS -----------------
function generateSessionId(): string {
  // Must only run client-side — session ID is never needed during SSR
  if (typeof window === 'undefined') {
    throw new Error(
      '[Consent] generateSessionId() called on server — this should never happen'
    );
  }

  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return 'anon_' + crypto.randomUUID();
  }

  // Fallback for older iOS WebKit (pre-15.4) and non-secure dev contexts
  return (
    'anon_' +
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    })
  );
}

function setCookie(value: string, days: number): void {
  if (typeof document === 'undefined') return; // SSR guard

  const expires = new Date();
  expires.setDate(expires.getDate() + days);

  document.cookie = [
    `${COOKIE_NAME}=${encodeURIComponent(value)}`,
    `expires=${expires.toUTCString()}`,
    'path=/',
    'SameSite=Lax',
    window.location.protocol === 'https:' ? 'Secure' : '',
  ]
    .filter(Boolean)
    .join('; ');
}

function getCookie(): string | null {
  if (typeof document === 'undefined') return null; // SSR guard

  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${COOKIE_NAME}=`));

  if (!match) return null;

  try {
    return decodeURIComponent(match.split('=')[1]);
  } catch {
    return null;
  }
}

function deleteCookie(): void {
  if (typeof document === 'undefined') return; // SSR guard
  document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

function getOrCreateSessionId(): string {
  if (_sessionId) return _sessionId;
  _sessionId = generateSessionId();
  return _sessionId;
}

function notify(): void {
  _listeners.forEach((l) => l(_state));
}

// ----------------- PUBLIC API -----------------
// Called once on app startup — reads existing cookie if present.
// Returns the resolved state so the Context provider can initialize
// synchronously without a separate getState() call.
export function initConsent(): ConsentState {
  // SSR guard — server always returns undecided, cookie is client-only
  if (typeof window === 'undefined') {
    return { status: 'undecided' };
  }

  // Reset module state on each init call to prevent stale state from
  // persisting across hot reloads (dev) or bfcache navigation (iOS Safari).
  // bfcache is very aggressive on older iOS — Safari restores the page from
  // memory on back/forward navigation without a full reload, which means
  // module-level variables from the previous session can still be in memory
  // while React re-mounts fresh, causing a mismatch.
  _state = { status: 'undecided' };
  _sessionId = null;

  const raw = getCookie();

  if (!raw) {
    _state = { status: 'undecided' };
    return _state;
  }

  try {
    const parsed: ConsentRecord = JSON.parse(raw);

    // Version mismatch — policy changed, re-collect consent
    if (parsed.version !== COOKIE_VERSION) {
      deleteCookie();
      _state = { status: 'undecided' };
      return _state;
    }

    _state = { status: 'decided', record: parsed };
    return _state;
  } catch {
    // Corrupt cookie — clear and start fresh
    deleteCookie();
    _state = { status: 'undecided' };
    return _state;
  }
}

export function getConsentState(): ConsentState {
  return _state;
}

export function hasAnalyticsConsent(): boolean {
  return _state.status === 'decided' && _state.record.categories.analytics;
}

// Core method called when user makes a choice in the banner.
// logToServer is injected from the Context layer which owns the Convex mutation.
export async function recordConsent(
  categories: ConsentCategory,
  action: ConsentAction,
  logToServer: (record: ConsentRecord) => Promise<void>
): Promise<void> {
  const record: ConsentRecord = {
    version: COOKIE_VERSION,
    timestamp: new Date().toISOString(),
    sessionId: getOrCreateSessionId(),
    categories,
    action,
  };

  // 1. Write cookie — user's choice is persisted locally first
  setCookie(JSON.stringify(record), COOKIE_EXPIRY_DAYS);

  // 2. Update in-memory state
  _state = { status: 'decided', record };

  // 3. Notify all subscribers (Context provider re-renders, GTM bridge updates)
  notify();

  // 4. Log to Convex
  try {
    await logToServer(record);
  } catch {
    console.warn('[Consent Manager] Server logging failed');
  }
}

/*
  Temporarily sets state to 'undecided' in memory only.
  Cookie is not touched — if the user dismisses the reopened banner without
  making a new choice, their old preference is still in the cookie and will
  be restored on the next initConsent() call.
*/
export function reopenConsent(): void {
  _state = { status: 'undecided' };
  notify();
}

// Subscribe to state changes — returns unsubscribe function for useEffect cleanup
export function subscribeToConsent(
  listener: (state: ConsentState) => void
): () => void {
  _listeners.push(listener);
  return () => {
    _listeners = _listeners.filter((l) => l !== listener);
  };
}
